/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience insights", description = "Audience insights give marketers deeper understanding of the composition of their audiences.", lastUpdatedDate = new Date(1749513600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience insights",
        description: "Audience insights give marketers deeper understanding of the composition of their audiences.",
        lastUpdatedDate: "Tue Jun 10 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Member preview",
          "depth": 0,
          "id": "member-preview"
        }, {
          "value": "Audience overlaps",
          "depth": 0,
          "id": "audience-overlaps"
        }, {
          "value": "Audience breakdowns",
          "depth": 0,
          "id": "audience-breakdowns"
        }, {
          "value": "Numerical metrics",
          "depth": 1,
          "id": "numerical-metrics"
        }, {
          "value": "Further exploration",
          "depth": 1,
          "id": "further-exploration"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Is our insight data saved by Hightouch?",
          "depth": 1,
          "id": "is-our-insight-data-saved-by-hightouch"
        }, {
          "value": "Do Breakdowns and Overlaps reflect if an audience belongs to a priority list?",
          "depth": 1,
          "id": "do-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      code: "code",
      img: "img",
      ul: "ul",
      li: "li",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Modern marketing teams coordinate omnichannel efforts in Hightouch by strategically building customer segments and activating them from their data warehouse to various destinations like Facebook, Iterable, Salesforce, Google, and more. When building large and complex audiences, it's often difficult to fully understand the users within the audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["Customer Studio's ", _jsx(_components.strong, {
          children: "Audience insights"
        }), " give marketing teams a clearer picture when creating, copying, or updating audiences.\nCustomer Studio also provides an ", _jsx(_components.a, {
          href: "/campaign-intelligence/insights",
          children: "analytics suite"
        }), " for more in-depth analysis."]
      }), "\n", _jsx(_components.h2, {
        id: "member-preview",
        children: "Member preview"
      }), "\n", _jsxs(_components.p, {
        children: ["When building an audience, it's helpful to have concrete examples of audience members to ensure the audience you're building is the intended one. You can more closely inspect your audience members by clicking ", _jsx(_components.code, {
          children: "Calculate size"
        }), ", which will open the members panel. After the first calculation or sync, we'll display the audience size and update it any time you open the members panel using the same button or sync the audience."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_audience_size.png",
          alt: "Previewing an audience in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The overview shows you a list of members. If you've chosen to ", _jsx(_components.a, {
          href: "/customer-studio/schema#column-configuration",
          children: "redact any columns"
        }), " in your model configuration, they are redacted here."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_member_list.png",
          alt: "Previewing an audience member list in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Clicking on a particular member will open the member details panel to give you an even deeper look at your audience members."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_member_details.png",
          alt: "Previewing an audience member in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "audience-overlaps",
        children: "Audience overlaps"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Audience Overlaps can only be set up between audiences that use the same\n", _jsx(_components.a, {
            href: "/customer-studio/schema#parent-models",
            children: "parent model"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "With many audiences syncing simultaneously, it can be difficult to ensure that end users aren't being targeted by multiple campaigns."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Audience overlaps"
        }), " help you validate the composition of your audiences by letting you compare one audience against another. You can find audience overlaps within the ", _jsx(_components.code, {
          children: "Show insights"
        }), " panel when defining your audience."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_show_insights.png",
          alt: "Previewing an audience in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Select a previously created audience from the dropdown to compare the audiences."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_overlap_example.png",
          alt: "Comparing audiences in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsxs(_components.a, {
          href: "https://hightouch.com/blog/feature-release-audience-overlaps",
          children: [_jsx(_components.strong, {
            children: "Audience Overlaps"
          }), " blog post"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "audience-breakdowns",
        children: "Audience breakdowns"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Breakdowns"
        }), " help quickly check your audience segmentation and ensure the audience meets your needs by enabling you to better understand the distribution of specific characteristics within audiences."]
      }), "\n", _jsxs(_components.p, {
        children: ["Breakdowns visually display the distribution of specific characteristics of a given audience, making it easier to validate that the audience reflects the desired segmentation before activation. You can find audience breakdowns within the ", _jsx(_components.code, {
          children: "Show insights"
        }), " panel when defining your audience."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_chart_breakdown_navigation.png",
          alt: "Setting an audience breakdown in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can select a metric from the following options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Saved metrics"
          }), ": ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/metrics",
            children: "custom metrics"
          }), " that are created in the workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events and Event properties"
          }), ": based on the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/schema#related-models-and-events",
            children: "event models"
          }), " connected to your parent model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Relations and Relation properties:"
          }), " based on the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/schema#related-models-and-events",
            children: "related models"
          }), " connected to your parent model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User properties:"
          }), " properties defined by your ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/schema#parent-models",
            children: "parent model"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Traits:"
          }), " ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/traits",
            children: "custom columns"
          }), " added to your parent model.\n", _jsx(_components.img, {
            src: "hightouch-audiences/audience-charts/small_chart_breakdown_options.png",
            alt: "Choosing an audience breakdown metric in the Hightouch UI"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "numerical-metrics",
        children: "Numerical metrics"
      }), "\n", _jsx(_components.p, {
        children: "When you select a numerical property or a model-based metric (e.g. events), you can optionally select a non-numerical user property to group the metric by."
      }), "\n", _jsx(_components.table, {
        children: _jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_chart_group_by_breakdown.png",
                alt: "Choosing a group by option for audience breakdown in the Hightouch UI"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_chart_group_by_state.png",
                alt: "Result of a group by for audience breakdown in the Hightouch UI"
              })
            })]
          })
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Except in the case of ", _jsx(_components.code, {
          children: "Saved metrics"
        }), ", if no ", _jsx(_components.code, {
          children: "Group by"
        }), " option is selected, the chart will group by the metric itself."]
      }), "\n", _jsx(_components.table, {
        children: _jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_chart_group_by_empty.png",
                alt: "Leaving group by empty for audience breakdown in the Hightouch UI"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_charts_no_group_by_chart.png",
                alt: "Result of no group by for audience breakdown in the Hightouch UI"
              })
            })]
          })
        })
      }), "\n", _jsx(_components.p, {
        children: "Depending on the type of numerical metric you select, you are able to choose additional options on what to view in your breakdown."
      }), "\n", _jsx(_components.table, {
        children: _jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_chart_options_for_metrics.png",
                alt: "Choosing aggregations in the Hightouch UI"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.img, {
                src: "hightouch-audiences/audience-charts/small_chart_options_for_metrics_2.png",
                alt: "Choosing aggregations in the Hightouch UI"
              })
            })]
          })
        })
      }), "\n", _jsx(_components.h3, {
        id: "further-exploration",
        children: "Further exploration"
      }), "\n", _jsxs(_components.p, {
        children: ["To explore a breakdown in more depth, you can open it in ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/campaign-intelligence/overview",
          children: "Charts"
        }), " to apply additional filters and groupings, explore different visualizations, and save the chart for future reference.\n", _jsx(_components.img, {
          src: "hightouch-audiences/audience-charts/small_charts_link_charts.png",
          alt: "Linking charts in the Hightouch UI"
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, check out the ", _jsxs(_components.a, {
          href: "https://hightouch.com/blog/audience-breakdowns",
          children: [_jsx(_components.strong, {
            children: "Audience Breakdowns"
          }), " blog post"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "is-our-insight-data-saved-by-hightouch",
        children: "Is our insight data saved by Hightouch?"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "No"
        }), ", Hightouch never saves your data."]
      }), "\n", _jsx(_components.h3, {
        id: "do-breakdowns-and-overlaps-reflect-if-an-audience-belongs-to-a-priority-list",
        children: "Do Breakdowns and Overlaps reflect if an audience belongs to a priority list?"
      }), "\n", _jsxs(_components.p, {
        children: ["Yes-the insight tools take ", _jsx(_components.a, {
          href: "/customer-studio/priority-lists",
          children: "audience priority"
        }), " into account.\nTo learn more, read ", _jsx(_components.a, {
          href: "/customer-studio/priority-lists",
          children: "the priority lists docs"
        }), " or navigate to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/priority-lists",
          children: [_jsx(_components.strong, {
            children: "Priority Lists"
          }), " page"]
        }), " and build your first priority list."]
      })]
    });
  }
}
export default MDXContent;
